import React, { useState, useEffect } from 'react';
import httpClient from '@assets/js/api/httpClient';

const CompFmtFile = ({ attrs, onChange, delConfirmMsg, atchFileId, atchFileSn, atchFileNm, filePathNm, onDeleteFile }) => {
    const {
        id,
        label,
        required = false,
        classCol = 'col-12',
        accept = '*/*',
        disabled = false
    } = attrs;

    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isLoadingImage, setIsLoadingImage] = useState(false);
    const [hasOriginalFile, setHasOriginalFile] = useState(false);
    const [loadError, setLoadError] = useState(null);

    // atchFileId가 있을 때 서버에서 이미지 로드
    useEffect(() => {
        const loadImageFromServer = async () => {
            if (!atchFileId) {
                setHasOriginalFile(false);
                setLoadError(null);
                return;
            }

            setIsLoadingImage(true);
            setLoadError(null);

            try {
                // console.log('이미지 로드 시작 - atchFileId:', atchFileId);

                const response = await httpClient.get('/image/view', {
                    params: { f: atchFileId },
                    responseType: 'blob'
                });

                console.log('서버 응답:', {
                    status: response.status,
                    contentType: response.headers['content-type'],
                    dataType: response.data?.type,
                    dataSize: response.data?.size
                });

                // blob 데이터 검증
                if (!response.data || response.data.size === 0) {
                    console.error('유효하지 않은 blob 데이터');
                    setLoadError('이미지 데이터가 비어있습니다.');
                    setHasOriginalFile(false);
                    setIsLoadingImage(false);
                    return;
                }

                // Content-Type이 이미지인지 확인
                const contentType = response.data.type || response.headers['content-type'];
                if (!contentType || !contentType.startsWith('image/')) {
                    // console.warn('이미지가 아닌 Content-Type:', contentType);
                    setLoadError(`지원하지 않는 파일 형식입니다. (${contentType})`);
                    setHasOriginalFile(false);
                    setIsLoadingImage(false);
                    return;
                }

                // blob을 URL로 변환
                const imageUrl = URL.createObjectURL(response.data);
                // console.log('Blob URL 생성 성공:', imageUrl);

                setPreviewUrl(imageUrl);
                setHasOriginalFile(true);
                setLoadError(null);
            } catch (error) {
                // console.error('이미지 로드 실패:', {
                //     message: error.message,
                //     response: error.response?.data,
                //     status: error.response?.status,
                //     headers: error.response?.headers
                // });

                let errorMessage = '이미지를 불러올 수 없습니다.';
                if (error.response?.status === 404) {
                    errorMessage = '이미지 파일을 찾을 수 없습니다.';
                } else if (error.response?.status === 403) {
                    errorMessage = '이미지에 접근할 권한이 없습니다.';
                } else if (error.message) {
                    errorMessage = `이미지 로드 실패: ${error.message}`;
                }

                setLoadError(errorMessage);
                setHasOriginalFile(false);
            } finally {
                setIsLoadingImage(false);
            }
        };

        loadImageFromServer();

        // cleanup: URL 메모리 해제
        return () => {
            if (previewUrl && previewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [atchFileId]);

    // 파일 선택 핸들러
    const handleFileChange = (e) => {
        const file = e.target.files[0];

        if (!file) {
            setSelectedFile(null);
            setPreviewUrl(null);
            return;
        }

        // 파일 확장자 검사 (png, jpg만 허용)
        const fileName = file.name.toLowerCase();
        const allowedExtensions = ['.png', '.jpg', '.jpeg'];
        const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

        if (!isValidExtension) {
            alert('PNG, JPG 파일만 업로드 가능합니다.');
            // input 초기화
            e.target.value = '';
            setSelectedFile(null);
            setPreviewUrl(null);
            return;
        }

        // 파일 타입 검사 (추가 보안)
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
        if (!allowedTypes.includes(file.type)) {
            alert('PNG, JPG 파일만 업로드 가능합니다.');
            // input 초기화
            e.target.value = '';
            setSelectedFile(null);
            setPreviewUrl(null);
            return;
        }

        // 기존 blob URL 정리 (서버에서 가져온 이미지)
        if (previewUrl && previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrl);
        }

        // 기존 파일이 있었고 새 파일을 선택한 경우 삭제 목록 생성
        if (hasOriginalFile && atchFileNm && filePathNm && onDeleteFile) {
            const deleteFileInfo = {
                fileId: atchFileId,
                fileSn: atchFileSn,
                fileNm: atchFileNm,
                fileUploadPath: filePathNm
            };
            onDeleteFile(deleteFileInfo);
        }

        setSelectedFile(file);
        setHasOriginalFile(false); // 새 파일 선택 시 기존 파일 플래그 해제

        // 이미지 파일인 경우 미리보기 생성
        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        } else {
            setPreviewUrl(null);
        }

        // 부모 컴포넌트에 파일 정보 전달
        if (onChange) {
            onChange({
                target: {
                    id: id,
                    value: file,
                    files: [file]
                }
            });
        }
    };

    // 파일 삭제 핸들러
    const handleRemoveFile = () => {
        // blob URL 정리 (서버에서 가져온 이미지)
        if (previewUrl && previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrl);
        }

        setSelectedFile(null);
        setPreviewUrl(null);

        // input 초기화
        const fileInput = document.getElementById(id);
        if (fileInput) {
            fileInput.value = '';
        }

        // 부모 컴포넌트에 빈 값 전달
        if (onChange) {
            onChange({
                target: {
                    id: id,
                    value: null,
                    files: []
                }
            });
        }
    };

    // 기존 이미지 삭제 핸들러
    const handleDeleteOriginalImage = () => {
        let conMsg = '이미지를 삭제하시겠습니까?';
        if (delConfirmMsg) { conMsg = delConfirmMsg; }
        if (!window.confirm(conMsg)) {
            return;
        }

        // 기존 파일 삭제 목록 생성
        if (hasOriginalFile && atchFileNm && filePathNm && onDeleteFile) {
            const deleteFileInfo = {
                fileId: atchFileId,
                fileSn: atchFileSn,
                fileNm: atchFileNm,
                fileUploadPath: filePathNm
            };
            onDeleteFile(deleteFileInfo);
        }

        // blob URL 정리
        if (previewUrl && previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrl);
        }

        setPreviewUrl(null);
        setHasOriginalFile(false);

        // input 초기화
        const fileInput = document.getElementById(id);
        if (fileInput) {
            fileInput.value = '';
        }
    };

    return (
        <div className={classCol}>
            <label htmlFor={id} className="form-label">
                {label}
                {required && <span className="text-danger ms-1">*</span>}
            </label>
            <div className="input-group has-validation">
                <input
                    type="file"
                    className="form-control"
                    id={id}
                    accept="image/png, image/jpeg, image/jpg"
                    disabled={disabled}
                    onChange={handleFileChange}
                    autoComplete="off"
                />
                {selectedFile && !disabled && (
                    <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={handleRemoveFile}
                        title="파일 삭제"
                        style={{
                            padding: '0.375rem 0.75rem',
                            paddingLeft: '0.5rem',
                            backgroundColor: '#ffffff',
                            color: '#6c757d',
                            borderColor: '#ced4da'
                        }}
                    >
                        <i className="bi bi-x-lg"></i>
                    </button>
                )}
                {required && <div className="invalid-feedback">파일을 선택해주세요.</div>}
            </div>

            {/* 허용 파일 형식 안내 */}
            <div className="mt-1">
                <small className="text-muted">
                    <i className="bi bi-info-circle me-1"></i>
                    허용 파일 형식: PNG, JPG
                </small>
            </div>

            {/* 선택된 파일 정보 표시 */}
            {selectedFile && (
                <div className="mt-2">
                    <small className="text-muted">
                        <i className="bi bi-file-earmark me-1"></i>
                        {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                    </small>
                </div>
            )}

            {/* 기존 이미지 정보 및 삭제 버튼 */}
            {!selectedFile && hasOriginalFile && previewUrl && !isLoadingImage && (
                <div className="mt-2 d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                        <i className="bi bi-image me-1"></i>
                        기존 이미지
                    </small>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={handleDeleteOriginalImage}
                        disabled={disabled}
                    >
                        <i className="bi bi-trash me-1"></i>
                        삭제
                    </button>
                </div>
            )}

            {/* 이미지 로딩 중 */}
            {isLoadingImage && (
                <div className="mt-3 text-center p-4" style={{
                    border: '1px dashed #dee2e6',
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa'
                }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">로딩 중...</span>
                    </div>
                    <p className="mt-2 mb-0 text-muted">이미지 로딩 중...</p>
                </div>
            )}

            {/* 이미지 로드 에러 */}
            {!isLoadingImage && loadError && (
                <div className="mt-3 p-3" style={{
                    border: '1px solid #dc3545',
                    borderRadius: '8px',
                    backgroundColor: '#f8d7da'
                }}>
                    <div className="d-flex align-items-center">
                        <i className="bi bi-exclamation-triangle-fill text-danger me-2" style={{ fontSize: '1.2rem' }}></i>
                        <span className="text-danger">{loadError}</span>
                    </div>
                    <small className="text-muted d-block mt-2">
                        <i className="bi bi-info-circle me-1"></i>
                        브라우저 개발자 도구(F12)의 콘솔에서 자세한 오류를 확인하세요.
                    </small>
                </div>
            )}

            {/* 이미지 미리보기 */}
            {!isLoadingImage && previewUrl && (
                <div className="mt-3 d-flex justify-content-center">
                    <img
                        src={previewUrl}
                        alt="미리보기"
                        style={{
                            width: '40%',
                            height: 'auto',
                            borderRadius: '8px',
                            border: '1px solid #dee2e6',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default CompFmtFile;
